import { PayloadAction } from "@reduxjs/toolkit";
import {
  OutputConfigBandMap,
  OutputConfigEditRootMap,
  OutputConfigRootMap
} from "../Api/Types";
import {
  AgeRange,
  AgeRangeType,
  AgeTypes
} from "../Banding/BandingAgeForm/Types";
import { EntryProps } from "../Banding/BandingEthnicityForm/Types";
import { BandingDetailsProps } from "../Banding/Helper";
import { FinalListItem } from "../FinalOutput/FinalOutputList/Data";
import { Navigation } from "../OutputConfigDialog/DialogNavTabs/Types";
import { OutputMappingItem } from "../OutputMapping/OutputMappingList/Types";
import { TableProps } from "../../../Common/UI/Index";
import {
  BandFields,
  BandingDetails,
  EditConfig,
  InitialState,
  MappingPopupToggle,
  SelectedConfigType
} from "./Types";

export const Reducer = {
  /**
   * To set the config list
   * @param state initial state
   * @param action payload action
   */
  setConfigList: (state: InitialState, action: PayloadAction<TableProps>): void => {
    state.configList = action.payload;
  },
  /**
   * To set the selected config ID
   * @param state initial state
   * @param action payload action
   */
  setSelectedConfig: (state: InitialState, action: PayloadAction<SelectedConfigType>): void => {
    state.selectedOutputConfig = action.payload;
  },
  /**
   * To update config name only
   * @param state initial state
   * @param action payload action
   */
  updateSelectedConfigName: (state: InitialState, action: PayloadAction<string>): void => {
    state.selectedOutputConfig = Object.assign({ ...state.selectedOutputConfig, name: action.payload });
  },
  /**
   * To set the banding details
   * @param state initial state
   * @param action payload action
   */
  setBandingDetails: (state: InitialState, action: PayloadAction<BandingDetails>): void => {
    state.bandingList = action.payload;
  },
  /**
   * To set the selected banding ID
   * @param state initial state
   * @param action payload action
   */
  setSelectedBandingDetails: (state: InitialState, action: PayloadAction<BandingDetailsProps>): void => {
    state.selectedBandingDetails = action.payload;
  },
  /**
   * To select Tab
   * @param state initial state value
   * @param action boolean values to set the flag
   */
  selectTab: (state: InitialState, action: PayloadAction<Navigation>): void => {
    state.selectedTab = action.payload;
  },
  /**
   * To select band fields
   * @param state initial state value
   * @param action boolean values to set the flag
   */
  updateSelectedGenders: (state: InitialState, action: PayloadAction<BandFields>): void => {
    const currentValues = [...state.selectedGenders];
    if(action.payload.isReset) {
      state.selectedGenders = [];
      return;
    }
    if(action.payload.checked) {
      if(currentValues.includes(action.payload.value)) return;
      state.selectedGenders = [...currentValues, action.payload.value];
    } else {
      const unCheckedBandFields = currentValues.filter((item: string) => (item !== action.payload.value));
      state.selectedGenders = unCheckedBandFields;
    }
  },
  /* To toggle and set output mapping popup
   * @param state initial state value
   * @param action Values to set popup status
   */
  setSelectedOutputMapping: (state: InitialState, action: PayloadAction<MappingPopupToggle>): void => {
    state.selectedOutputMapping = action.payload;
  },
  /**
   * To set search value in output mapping
   * @param state initial state value
   * @param action set search term
   */
  setSearchTerm: (state: InitialState, action: PayloadAction<string>): void => {
    state.mappingFilters = Object.assign(state.mappingFilters, { searchTerm: action.payload });
  },
  /**
   * To set flag to show bandings
   * @param state initial state value
   * @param action set flag to show bandings
   */
  setShowBandings: (state: InitialState, action: PayloadAction<boolean>): void => {
    state.mappingFilters = Object.assign(state.mappingFilters, { showBandings: action.payload });
  },
  /**
   * To set the selected age range from
   * @param state initial state value
   * @param action set flag to show bandings
   */
  setSelectedAgeRangeFrom: (state: InitialState, action: PayloadAction<AgeRange>): void => {
    state.selectedAgeRangeFrom = action.payload;
  },
  /**
   * To set the selected age range to
   * @param state initial state value
   * @param action set flag to show bandings
   */
  setSelectedAgeRangeTo: (state: InitialState, action: PayloadAction<AgeRange>): void => {
    state.selectedAgeRangeTo = action.payload;
  },
  /**
   * To set the selected age value from
   * @param state initial state value
   * @param action set flag to show bandings
   */
  setSelectedAgeValueFrom: (state: InitialState, action: PayloadAction<AgeRangeType>): void => {
    state.selectedAgeValueFrom = action.payload;
  },
  /**
   * To set the selected age value to
   * @param state initial state value
   * @param action set flag to show bandings
   */
  setSelectedAgeValueTo: (state: InitialState, action: PayloadAction<AgeRangeType>): void => {
    state.selectedAgeValueTo = action.payload;
  },
  /*
   * To set output mapping list
   * @param state initial state value
   * @param action payload action
   */
  setOutputMappingList: (state: InitialState, action: PayloadAction<OutputMappingItem[]>): void => {
    state.outputMappingList = action.payload;
  },
  /**
   * To set flag of popup save button
   * @param state initial state value
   * @param action payload action
   */
  setEnableMappingPopupSave: (state: InitialState, action: PayloadAction<boolean>): void => {
    state.enableMappingPopupSave = action.payload;
  },
  /**
   * To set output mapping popup band data
   * @param state initial state value
   * @param action payload action
   */
  setOutputMappingPopupBandData: (state: InitialState, action: PayloadAction<OutputConfigBandMap[]>): void => {
    state.outputMappingPopupBandData = action.payload;
  },
  /**
   * To set output mapping popup config data
   * @param state initial state value
   * @param action payload action
   */
  setOutputMappingPopupConfigData: (state: InitialState, action: PayloadAction<OutputConfigRootMap>): void => {
    state.outputMappingPopupConfigData = action.payload;
  },
  /**
   * To set output mapping popup config data
   * @param state initial state value
   * @param action payload action
   */
  setOutputMappingPopupEditConfigData: (state: InitialState, action: PayloadAction<OutputConfigEditRootMap>): void => {
    state.outputMappingPopupEditConfigData = action.payload;
  },
  /**
   * To set the form data
   * @param state initial state value
   * @param action action payload
   */
  setFormData: (state: InitialState, action: PayloadAction<FormData>): void => {
    state.formData = action.payload;
  },
  /**
   * To set the edit config
   * @param state initial state value
   * @param action action payload
   */
  setEditConfig: (state: InitialState, action: PayloadAction<EditConfig>): void => {
    state.editConfig = action.payload;
  },
  /**
   * To set ethinicity banding entry
   * @param state initial state value
   * @param action action payload
   */
  setEntryData: (state: InitialState, action: PayloadAction<EntryProps>): void => {
    state.entryData = action.payload;
  },
  /*
   * To set final output list
   * @param state initial state value
   * @param action payload action
   */
  setFinalOutputList: (state: InitialState, action: PayloadAction<FinalListItem[]>): void => {
    state.outputElements = action.payload;
  },
  /**
   * To set final output filter by
   * @param state initial state value
   * @param action payload action
   */
  setFinalOutputFilterBy: (state: InitialState, action: PayloadAction<string>): void => {
    state.finalOutputFilterBy = action.payload;
  },
  /**
   * To set id that has to be focussed in output config page
   * @param state initial state value
   * @param action payload action
   */
  setFocusConfigId: (state: InitialState, action: PayloadAction<string>): void => {
    state.focusConfigId = action.payload;
  },
  /**
   * To set search by tag name
   * @param state initial state value
   * @param action payload action
   */
  setSearchByTagName: (state: InitialState, action: PayloadAction<string>): void => {
    state.searchByTagName = action.payload;
  },
  /**
   * To set the from type
   * @param state initial state value
   * @param action payload action
   */
  setSelectedFromType: (state: InitialState, action: PayloadAction<AgeTypes>): void => {
    state.selectedFromType = action.payload;
  },
  /**
   * To set the to type
   * @param state initial state value
   * @param action payload action
   */
  setSelectedToType: (state: InitialState, action: PayloadAction<AgeTypes>): void => {
    state.selectedToType = action.payload;
  },
};
